import React from 'react';
import {
  Box,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import useIntl from '../../hooks/useIntl';
import messages from './messages';

// ENG-34128 — the parked buckets below the funnel: On Hold leads and Waitlist
// applicants, lifted out of the active flow. Deliberately small and de-emphasised —
// a lesser category than any active stage — and color-distinct from the pipeline.
//
// DEVIATION (matches Funnel.js GROUP_ACCENTS): these are literal hues rather than
// theme palette tokens, because the shared palette exposes no distinct "parked"
// accents. Indigo = Waitlist, amber = On Hold (amber, not an alarm red). Matched
// light/dark variants.
const ACCENTS = {
  waitlist: {
    light: { main: '#5b53c6', soft: 'rgba(91,83,198,0.10)' },
    dark: { main: '#a99ef0', soft: 'rgba(169,158,240,0.14)' },
  },
  'on-hold': {
    light: { main: '#a9690d', soft: 'rgba(169,105,13,0.10)' },
    dark: { main: '#e0a949', soft: 'rgba(224,169,73,0.14)' },
  },
};

const BUCKET_COPY = {
  waitlist: { label: messages.waitlistBucket, unit: messages.waitlistBucketUnit },
  'on-hold': { label: messages.onHoldBucket, unit: messages.onHoldBucketUnit },
};

const ParkedBucketRow = ({ bucket, selected, onSelect, intl }) => {
  const accent = ACCENTS[bucket.key];
  const copy = BUCKET_COPY[bucket.key];
  if (!accent || !copy) return null;

  return (
    <Box
      component="button"
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(bucket.key)}
      sx={(theme) => {
        const tint = accent[theme.palette.mode] || accent.light;
        return {
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          px: 1.25,
          py: 0.75,
          borderRadius: 1,
          border: '1px solid',
          borderColor: tint.main,
          borderLeftWidth: 4,
          backgroundColor: selected ? 'action.selected' : tint.soft,
          '&:hover': { backgroundColor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: tint.main },
        };
      }}
    >
      <Typography
        variant="labelSmall"
        sx={(theme) => ({
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: (accent[theme.palette.mode] || accent.light).main,
        })}
      >
        {intl.formatMessage(copy.label)}
      </Typography>
      <Typography
        variant="bodyMedium"
        sx={{ ml: 'auto', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
      >
        {intl.formatMessage(copy.unit, { count: bucket.count })}
      </Typography>
    </Box>
  );
};

// The parked section: a quiet heading plus one small row per bucket, in the order
// the hook supplies (Waitlist above On Hold). Rendered below the funnel; each row
// reuses the same drill-down panel as a stage via onSelect(key).
const ParkedBuckets = ({ buckets, selectedStageKey, onSelect }) => {
  const intl = useIntl();
  if (!buckets || buckets.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Typography
        variant="labelSmall"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled' }}
      >
        {intl.formatMessage(messages.parkedHeading)}
      </Typography>
      {buckets.map((bucket) => (
        <ParkedBucketRow
          key={bucket.key}
          bucket={bucket}
          selected={bucket.key === selectedStageKey}
          onSelect={onSelect}
          intl={intl}
        />
      ))}
    </Stack>
  );
};

export default ParkedBuckets;
